/**
 * ПОЛЕ ВВОДА, КОТОРОЕ НЕ ЯВЛЯЕТСЯ ПОЛЕМ ВВОДА
 *
 * Строка ввода сообщения — не «textarea», а обычный узел страницы,
 * в котором разрешено печатать. Так сделано во всех веб-мессенджерах,
 * и причина не в красоте.
 *
 * Браузер на телефоне пристраивает над клавиатурой свою полосу со
 * стрелками «предыдущее поле», «следующее поле» и галочкой «готово» —
 * она нужна, чтобы прыгать по полям анкеты. В переписке прыгать некуда:
 * полоса просто занимает место между строкой ввода и клавиатурой.
 * Узел с разрешённым вводом браузеры анкетой не считают и полосу
 * для него не показывают.
 *
 * Плата за это — работа с курсором руками: у обычного поля есть
 * «сколько символов слева», у узла страницы — дерево узлов и
 * выделение. Всё, что для этого нужно, собрано здесь.
 */

/** Неразрывный пробел: браузер ставит его сам, а человек набирал обычный. */
const NBSP = ' '

/**
 * Текст поля.
 *
 * Берём `innerText`, а не `textContent`: первый понимает перенос
 * строки, второй склеит строки в одну.
 */
export function readText(node: HTMLElement): string {
  return node.innerText.split(NBSP).join(' ')
}

/**
 * Где стоит курсор — в символах от начала.
 *
 * Верно ровно до тех пор, пока внутри поля один кусок текста и ничего
 * больше: тогда «длина текста слева» и «сколько символов слева» — одно
 * и то же. За этим следит `flatten`.
 */
export function caretAt(node: HTMLElement): number {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return readText(node).length

  const range = selection.getRangeAt(0)
  if (!node.contains(range.endContainer)) return readText(node).length

  const upToCaret = range.cloneRange()
  upToCaret.selectNodeContents(node)
  upToCaret.setEnd(range.endContainer, range.endOffset)
  return upToCaret.toString().length
}

/** Поставить курсор на нужный символ. */
export function putCaret(node: HTMLElement, offset: number): void {
  const selection = window.getSelection()
  if (!selection) return

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT)
  let seen = 0
  let target: Text | null = null
  let inside = 0

  while (walker.nextNode()) {
    const text = walker.currentNode as Text
    const length = text.data.length
    if (seen + length >= offset) {
      target = text
      inside = offset - seen
      break
    }
    seen += length
  }

  const range = document.createRange()
  if (target) {
    range.setStart(target, Math.max(0, Math.min(inside, target.data.length)))
  } else {
    // Пустое поле или курсор за концом: становимся в самый конец
    range.selectNodeContents(node)
    range.collapse(false)
  }
  range.collapse(true)

  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Заменить весь текст и поставить курсор.
 *
 * Нужно там, где текст меняем мы, а не человек: восстановили черновик,
 * вошли в правку, подставили имя после «@».
 */
export function writeText(node: HTMLElement, text: string, caret?: number): void {
  node.textContent = text
  putCaret(node, caret ?? text.length)
}

/** Внутри поля один кусок текста и ничего больше. */
export function isFlat(node: HTMLElement): boolean {
  return (
    node.childNodes.length === 0 ||
    (node.childNodes.length === 1 && node.firstChild?.nodeType === Node.TEXT_NODE)
  )
}

/**
 * Распрямить поле.
 *
 * На перенос строки браузер заводит внутри поля свои узлы — и у каждого
 * браузера свои. Человеку это незаметно, а нам мешает: место курсора мы
 * считаем длиной текста слева, и каждый такой узел сбивает счёт на
 * символ. Подсказка имён после «@» начинала подставлять не туда, стоило
 * написать сообщение в две строки.
 *
 * Место курсора узнаём отметкой: ставим в него невидимый символ, читаем
 * текст целиком — и отметка сама показывает, где мы стоим. Считать
 * вручную нельзя: где именно браузер видит перенос строки, знает
 * только он сам.
 *
 * Трогаем поле только когда оно перестало быть плоским, то есть после
 * переноса строки или вставки. При обычном наборе не вмешиваемся вовсе:
 * подмена содержимого сбивает подсказки клавиатуры и набор иероглифов.
 */
export function flatten(node: HTMLElement): void {
  if (isFlat(node)) return

  const selection = window.getSelection()
  const inside =
    selection !== null &&
    selection.rangeCount > 0 &&
    node.contains(selection.getRangeAt(0).endContainer)

  if (!selection || !inside) {
    node.textContent = readText(node)
    return
  }

  // Символ, которого не бывает в наборе руками
  const mark = String.fromCharCode(1)
  const range = selection.getRangeAt(0).cloneRange()
  range.collapse(false)
  range.insertNode(document.createTextNode(mark))

  const marked = node.innerText.split(NBSP).join(' ')
  const at = marked.indexOf(mark)
  const text = marked.split(mark).join('')

  node.textContent = text
  putCaret(node, at < 0 ? text.length : at)
}

/**
 * Напечатать за человека — так, как если бы он набрал сам.
 *
 * Через `insertText`, а не подменой содержимого: иначе ломается отмена
 * по Ctrl+Z и сбивается подсказка клавиатуры на телефоне. Способ
 * считается устаревшим, но замены ему нет, и работает он везде.
 */
export function typeText(text: string): void {
  document.execCommand('insertText', false, text)
}
